import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Album, ServiceAuthType, ServiceProvider, User, UserTeamStatus } from '@uploadcam/common';
import { LogoutInteractor } from '../../../../features/auth/domain/interactors/logout.interactor';
import { AUTH_LOGIN_URL, ONBOARD_TEAM_MICROSOFT_SITES } from '../../../app-routing.module';
import { getLogger } from '../../../core/utils';
import { FeedFilterQueryParams } from '../../feed-shared/data/feed-filter-query-params';
import { GetUserInteractor } from '../../../../features/auth/domain/interactors/get-user.interactor';
import { GetCredentialInteractor } from '../../../../features/auth/domain/interactors/get-credential.interactor';
import { AnalyticsService } from './analytics.service';

@Injectable()
export class NavigationService {
  private readonly logger = getLogger('NavigationService');

  constructor(
    private readonly analytics: AnalyticsService,
    private readonly getCredential: GetCredentialInteractor,
    private readonly getUser: GetUserInteractor,
    private readonly logoutInteractor: LogoutInteractor,
    private readonly router: Router,
  ) {}

  public getCurrentUrl(): string {
    return this.router.url;
  }

  public getUserHomeUrl(user: User): string {
    const credential = this.getCredential.execute();
    const teamStatusUrlMap: Record<UserTeamStatus, string> = {
      [UserTeamStatus.Manager]: user.isFeedManager ? '/feed/internal' : '/team',
      [UserTeamStatus.Member]: user.isFirstLogin ? '/member' : '/feed/internal',
      [UserTeamStatus.NoTeam]:
        '/onboard/create-team' +
        (credential.authType === ServiceAuthType.MicrosoftTeamSites
          ? `/${ONBOARD_TEAM_MICROSOFT_SITES}`
          : ''),
      [UserTeamStatus.PendingInvite]: '/onboard/invite',
    };

    return teamStatusUrlMap[user.teamStatus];
  }

  public async logout(): Promise<void> {
    let provider: ServiceProvider | undefined;

    try {
      const user = await this.getUser.execute();
      provider = user.lastLoginProvider;
    } catch (err) {
      this.logger.warn('Logout: User is not logged in');
    }

    this.analytics.track('logout_start', { provider });
    this.analytics.clearUserTags();

    try {
      await this.logoutInteractor.execute();

      if (await this.router.navigate([AUTH_LOGIN_URL])) {
        this.analytics.track('logout_completed', { provider });
      } else {
        this.analytics.track('logout_error', { provider });
      }
    } catch (err) {
      this.analytics.track('logout_error', { provider });
      throw err;
    }
  }

  public goToPostCreateTeamLanding(): void {
    this.albums();
  }

  public albums(): void {
    this.router.navigate(['/albums']);
  }

  public manageTeam(): void {
    this.router.navigate(['/team']);
  }

  public onboardSurvey(): void {
    this.router.navigate(['/onboard/survey']);
  }

  public inviteUsers(): void {
    this.router.navigate(['/onboard/invite-users']);
  }

  public async redirectToUserHome(): Promise<void> {
    const user = await this.getUser.execute();
    this.logger.info(`Team status: ${user.teamStatus}`);
    this.router.navigate([this.getUserHomeUrl(user)]);
  }

  public upgrade(): void {
    this.router.navigate(['/purchase']);
  }

  private viewActivityLog(filter: Partial<FeedFilterQueryParams>): void {
    const queryParams: FeedFilterQueryParams = {
      uploadedBy: '',
      albumId: '',
      folderId: '',
      folderName: '',
      afterDate: '',
      beforeDate: '',
      geoBBox: '',
      tags: '',
      ...filter,
    };

    this.router.navigate(['/feed/internal'], { queryParams });
  }

  public viewAlbumActivity(album: Album): void {
    this.viewActivityLog({ albumId: album.id });
  }

  public viewUserActivity(userId: string): void {
    this.viewActivityLog({ uploadedBy: userId });
  }
}
